import React, { useState } from 'react'
import { useNavigation, useTheme } from '@react-navigation/native'
import { ActivityIndicator, Image, StyleSheet, TouchableOpacity } from 'react-native'
import { View } from 'react-native'
import { Theme } from '@theme'
import { useCurrentUserStore } from '@store'
import { Icon, Text } from '../atoms'
import { Button } from '../molecules'
import { getCommonStyles } from '@utils'
import {
  useExtension,
  usePaginateMyMemories,
  usePaginateAlbums,
  usePaginateAlbumMemories,
  useScreen,
  Album,
  Memory,
  openImageLibraryMultiple,
} from '@hooks'
import { getOrganismsStyles } from './style'
import { useLanguage } from '@context'

// ---------- AlbumTile ----------
// Renders a Facebook-style album tile: multi-image cover + title + item count
const AlbumTile = ({ onPress, album }: { album: Album; onPress: () => void }) => {
  const theme = useTheme()
  const s = getTileStyle(theme)
  const { flatData: memories, totalCount } = usePaginateAlbumMemories({ albumId: album.id })
  const coverUrls = memories.slice(0, 4).map((m) => m.mediaUrl)
  const count = coverUrls.length

  const renderCover = () => {
    if (count === 0) {
      return (
        <View style={[s.cover, s.coverEmpty]}>
          <Icon name="picture" color={theme.colors.greyBlue} size={32} />
        </View>
      )
    }
    if (count === 1) {
      return (
        <View style={s.cover}>
          <Image
            source={{ uri: coverUrls[0] }}
            style={StyleSheet.absoluteFillObject}
            resizeMode="cover"
          />
        </View>
      )
    }
    if (count === 2) {
      return (
        <View style={[s.cover, s.coverRow]}>
          {coverUrls.map((uri, i) => (
            <Image key={i} source={{ uri }} style={s.halfImage} resizeMode="cover" />
          ))}
        </View>
      )
    }
    if (count === 3) {
      return (
        <View style={[s.cover, s.coverRow]}>
          <Image source={{ uri: coverUrls[0] }} style={s.halfImage} resizeMode="cover" />
          <View style={s.halfColumn}>
            <Image source={{ uri: coverUrls[1] }} style={s.quarterImage} resizeMode="cover" />
            <Image source={{ uri: coverUrls[2] }} style={s.quarterImage} resizeMode="cover" />
          </View>
        </View>
      )
    }
    // 4 images: 2×2 grid
    return (
      <View style={[s.cover, s.coverGrid]}>
        {coverUrls.map((uri, i) => (
          <Image key={i} source={{ uri }} style={s.quarterGridImage} resizeMode="cover" />
        ))}
      </View>
    )
  }

  const itemLabel = totalCount === 1 ? '1 Item' : `${totalCount} Items`

  return (
    <TouchableOpacity style={s.tile} onPress={onPress} activeOpacity={0.85}>
      {renderCover()}
      <Text type="body" numberOfLines={1} style={s.title}>
        {album.title}
      </Text>
      <Text type="bodySmall" color={theme.colors.greyBlue} numberOfLines={1} style={s.title}>
        {itemLabel}
      </Text>
    </TouchableOpacity>
  )
}

// ---------- CreateAlbumTile ----------
const CreateAlbumTile = ({ onPress }: { onPress: () => void }) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const s = getTileStyle(theme)

  return (
    <TouchableOpacity style={s.tile} onPress={onPress} activeOpacity={0.85}>
      <View style={[s.cover, s.coverEmpty]}>
        <Icon name="plus" color={theme.colors.greyBlue} size={28} />
      </View>
      <Text type="body" numberOfLines={1} style={s.title}>
        {t('albums.createAlbum')}
      </Text>
      <Text type="bodySmall" style={s.title}>
        {' '}
      </Text>
    </TouchableOpacity>
  )
}

// ---------- AlbumsGrid ----------
const AlbumsGrid = ({
  albums,
  showCreate,
  numColumns,
  gap,
  onCreatePress,
  onAlbumPress,
}: {
  albums: Album[]
  showCreate: boolean
  numColumns: number
  gap: number
  onCreatePress: () => void
  onAlbumPress: (album: Album) => void
}) => {
  const tilePercent = `${100 / numColumns}%` as const

  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', padding: gap / 2 }}>
      {showCreate && (
        <View style={{ width: tilePercent, padding: gap / 2 }}>
          <CreateAlbumTile onPress={onCreatePress} />
        </View>
      )}
      {albums.map((album, index) => (
        <View key={album.id || index} style={{ width: tilePercent, padding: gap / 2 }}>
          <AlbumTile album={album} onPress={() => onAlbumPress(album)} />
        </View>
      ))}
    </View>
  )
}

// ---------- MemoriesGrid ----------
const MemoriesGrid = ({
  memories,
  isMe,
  numColumns,
  gap,
  onMemoryPress,
  onAddPress,
}: {
  memories: Memory[]
  isMe: boolean
  numColumns: number
  gap: number
  onMemoryPress: (memory: Memory) => void
  onAddPress: () => void
}) => {
  const theme = useTheme()
  const s = getTileStyle(theme)
  const tilePercent = `${100 / numColumns}%` as const

  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', padding: gap / 2 }}>
      {isMe && (
        <View style={{ width: tilePercent, padding: gap / 2 }}>
          <TouchableOpacity style={s.tile} onPress={onAddPress} activeOpacity={0.85}>
            <View style={[s.cover, s.coverEmpty]}>
              <Icon name="plus" color={theme.colors.greyBlue} size={28} />
            </View>
          </TouchableOpacity>
        </View>
      )}
      {memories.map((memory, index) => (
        <View key={memory.id || index} style={{ width: tilePercent, padding: gap / 2 }}>
          <TouchableOpacity
            style={s.tile}
            onPress={() => onMemoryPress(memory)}
            activeOpacity={0.85}
          >
            <View style={s.cover}>
              <Image
                source={{ uri: memory.mediaUrl }}
                style={StyleSheet.absoluteFillObject}
                resizeMode="cover"
              />
            </View>
          </TouchableOpacity>
        </View>
      ))}
    </View>
  )
}

// ---------- TabBar ----------
type SelfTab = 'personal' | 'shared' | 'albums'

const TabBar = <T extends string>({
  tabs,
  activeTab,
  labels,
  onSelect,
}: {
  tabs: T[]
  activeTab: T
  labels: Record<string, string>
  onSelect: (tab: T) => void
}) => {
  const theme = useTheme()

  return (
    <View style={tabBarStyles.container}>
      {tabs.map((tab) => {
        const isActive = tab === activeTab
        return (
          <TouchableOpacity
            key={tab}
            style={[
              tabBarStyles.tab,
              isActive && { borderBottomColor: theme.colors.primary, borderBottomWidth: 2 },
            ]}
            onPress={() => onSelect(tab)}
          >
            <Text
              type={isActive ? 'bodyBold' : 'body'}
              color={isActive ? theme.colors.primary : theme.colors.greyBlue}
            >
              {labels[tab]}
            </Text>
          </TouchableOpacity>
        )
      })}
    </View>
  )
}

const tabBarStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: 'transparent',
    marginTop: 8,
  },
  tab: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
})

// ---------- Memories (main export) ----------
export const Memories = ({
  profileId,
  contactAccountId,
}: {
  profileId: string
  contactAccountId?: string
}) => {
  const navigation = useNavigation()
  const theme = useTheme()
  const { t } = useLanguage()
  const commonStyles = getCommonStyles(theme)
  const organismsStyles = getOrganismsStyles(theme)

  const { userID } = useCurrentUserStore()
  /** Tab navigator passes no profileId; some routes use "me". Must match UUID or sentinel or own tab. */
  const isMe =
    profileId === userID || profileId === 'me' || (profileId == null && contactAccountId == null)

  const { isMobile } = useScreen()
  const { isExtension } = useExtension()

  const [selfTab, setSelfTab] = useState<SelfTab>('personal')

  const { flatData: allMemories, isFetching: isMemoriesFetching } = usePaginateMyMemories()
  const { flatData: albums, isFetching: isAlbumsFetching } = usePaginateAlbums()

  // Self-profile filtering (my-memories API only returns the current user's rows)
  const personalMemories = allMemories.filter(
    (m) => (m.sharedWithAccountIds?.length ?? 0) === 0 && !m.albumId,
  )
  const sharedMemories = allMemories.filter((m) => (m.sharedWithAccountIds?.length ?? 0) > 0)

  // Contact-profile filtering
  const contactSharedMemories = contactAccountId
    ? allMemories.filter(
        (m) =>
          (m.sharedWithAccountIds?.includes(contactAccountId) ?? false) ||
          m.createdByAccountId === contactAccountId,
      )
    : []

  const goToAddMemory = async () => {
    const uris = await openImageLibraryMultiple()
    if (uris.length === 0) return

    if (isExtension) {
      ;(navigation as any).navigate('Profile', {
        screen: 'ProfileAddMemory',
        params: { profileId, preselectedImages: uris },
      })
    } else {
      ;(navigation as any).navigate(
        'ProfileAddMemory',
        isMe ? { preselectedImages: uris } : { profileId, preselectedImages: uris },
      )
    }
  }

  const goToAlbum = (album: Album) => {
    ;(navigation as any).navigate('ProfileAlbumMemories', { albumId: album.id, readonly: !isMe })
  }

  const goToMemory = (memory: Memory) => {
    ;(navigation as any).navigate('ProfileShowMemory', { memoryId: memory.id, readonly: !isMe })
  }

  const { isTablet } = useScreen()
  const numColumns = isMobile ? 3 : isTablet ? 4 : 6
  const gap = 12

  const isLoading = isMemoriesFetching || isAlbumsFetching

  if (isMe) {
    const selfLabels: Record<SelfTab, string> = {
      personal: t('common.personal'),
      shared: t('common.shared'),
      albums: t('albums.title'),
    }

    return (
      <View>
        <Button
          isSmall
          style={[organismsStyles.buttonAdd, !isMobile && commonStyles.alignSelfCenter]}
          title={t('memoriesForm.addPhoto')}
          textColor={theme.colors.primary}
          isIconRight
          iconName="plus"
          iconColor={theme.colors.primary}
          onPress={goToAddMemory}
        />
        <TabBar
          tabs={['personal', 'shared', 'albums'] as SelfTab[]}
          activeTab={selfTab}
          labels={selfLabels}
          onSelect={setSelfTab}
        />
        <View>
          {selfTab === 'personal' && (
            <MemoriesGrid
              memories={personalMemories}
              isMe={isMe}
              numColumns={numColumns}
              gap={gap}
              onMemoryPress={goToMemory}
              onAddPress={goToAddMemory}
            />
          )}
          {selfTab === 'shared' && (
            <MemoriesGrid
              memories={sharedMemories}
              isMe={isMe}
              numColumns={numColumns}
              gap={gap}
              onMemoryPress={goToMemory}
              onAddPress={goToAddMemory}
            />
          )}
          {selfTab === 'albums' && (
            <AlbumsGrid
              albums={albums}
              showCreate
              numColumns={numColumns}
              gap={gap}
              onCreatePress={goToAddMemory}
              onAlbumPress={goToAlbum}
            />
          )}
          {isLoading && (
            <ActivityIndicator
              size="small"
              color={theme.colors.primary}
              style={{ marginTop: 16 }}
            />
          )}
        </View>
      </View>
    )
  }

  // Contact profile: shared memories only (no tabs)
  // Tagging needs a stable id to drop into sharedWithAccountIds — accountId
  // for a real contact, ghost-profile id for a ghost (polymorphic column).
  const canAddTaggedMemory = !!contactAccountId

  return (
    <View>
      {canAddTaggedMemory && (
        <Button
          isSmall
          style={[organismsStyles.buttonAdd, !isMobile && commonStyles.alignSelfCenter]}
          title={t('memoriesForm.addPhoto')}
          textColor={theme.colors.primary}
          isIconRight
          iconName="plus"
          iconColor={theme.colors.primary}
          onPress={goToAddMemory}
        />
      )}
      <View>
        <MemoriesGrid
          memories={contactSharedMemories}
          isMe={false}
          numColumns={numColumns}
          gap={gap}
          onMemoryPress={goToMemory}
          onAddPress={goToAddMemory}
        />
        {isLoading && (
          <ActivityIndicator size="small" color={theme.colors.primary} style={{ marginTop: 16 }} />
        )}
      </View>
    </View>
  )
}

// ---------- Tile styles ----------
const getTileStyle = (theme: Theme) =>
  StyleSheet.create({
    tile: {
      width: '100%',
      borderRadius: theme.border.small,
      borderWidth: 1,
      borderColor: theme.colors.grey20,
      overflow: 'hidden',
    },
    cover: {
      width: '100%',
      aspectRatio: 1,
      borderRadius: 0,
      overflow: 'hidden',
      backgroundColor: theme.colors.grey10,
    },
    coverEmpty: {
      justifyContent: 'center',
      alignItems: 'center',
    },
    coverRow: {
      flexDirection: 'row',
    },
    coverGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
    },
    halfImage: {
      width: '50%',
      height: '100%',
    },
    halfColumn: {
      width: '50%',
      height: '100%',
    },
    quarterImage: {
      width: '100%',
      height: '50%',
    },
    quarterGridImage: {
      width: '50%',
      height: '50%',
    },
    title: {
      marginTop: 6,
      marginBottom: 6,
      paddingHorizontal: 6,
    },
  })
